import ReactEChartsCore from 'echarts-for-react/lib/core';
import echarts from '@sctech/echarts';
import { useEffect, useRef } from 'react';
import classes from './Echarts.module.css';
import Typography from '../Typography/Typography';
import _ from 'lodash';
import { truncateNumber } from '../../utils/numberHelper';

const customColors = ['#008ffb', '#26e7a6', '#EE6666', '#FAC858', '#73C0DE', '#3BA272', '#FC8452', '#9A60B4', '#EA7CCC'];
echarts.registerTheme('custom', {
    color: customColors,
});

const getDynamicYAxisPadding = (series = []) => {
    const yAxisProps = {};
    const maxFromMultipleSeries = [];

    series.map((s) => {
        const ySeries = _.map(s.data, (v) => _.get(v, [1]));
        const maxDataPoint = _.max(ySeries);
        if (maxDataPoint) maxFromMultipleSeries.push(maxDataPoint);
    });

    const overAllMaxValue = _.max(maxFromMultipleSeries);

    if (overAllMaxValue) yAxisProps.max = overAllMaxValue + truncateNumber(overAllMaxValue / 10, 2);

    return yAxisProps;
};

const EChart = ({
    series = [],
    group,
    title,
    lastReported,
    subtext,
    xFormatter,
    yFormatter,
    type = 'line',
    lineType,
    step = false,
    lineStyle = {},
    yAxisProps = {},
    xAxisProps = {},
    gridProps = {},
    annotations = [],
    rightSideElement,
    showToolBox = true,
    showTooltip = true,
    style = {},
    label = {},
    addYAxisDynamicPadding = false,
    zoomOnMouseWheel = true,
    yTooltipSuffix = [''],
}) => {
    const chartRef = useRef();

    useEffect(() => {
        if (!chartRef.current) return;
        const instance = chartRef.current.getEchartsInstance();

        if (group) {
            instance.group = group;
            echarts.connect(group);
        }

        const handleWheel = (e) => {
            const dataZooms = option.dataZoom || [];
            const zoomInside = dataZooms.find((z) => z.type === 'inside');
            const zoomDisabled = !zoomInside || zoomInside.zoomOnMouseWheel === false;

            if (zoomDisabled) {
                e.stopImmediatePropagation();
            }
        };

        const dom = instance.getZr().dom;
        dom.addEventListener('wheel', handleWheel, { passive: true, capture: true });

        return () => {
            dom.removeEventListener('wheel', handleWheel, { capture: true });
            if (group) {
                echarts.disconnect(group);
            }
        };
    }, [group, zoomOnMouseWheel]);

    useEffect(() => {
        const chart = chartRef.current?.getEchartsInstance();
        if (chart) {
            chart.dispatchAction({
                type: 'dataZoom',
                start: 0,
                end: 100,
            });
        }
    }, [_.get(series, [0])]);

    const option = {
        grid: {
            left: 0,
            right: 0,
            bottom: 1,
            containLabel: true,
            ...gridProps,
        },
        tooltip: {
            show: showTooltip,
            trigger: 'axis',
            formatter: (params) => {
                if (params.length > 0) {
                    const xValue = params[0].axisValue;
                    const xFormatted = xFormatter ? xFormatter(xValue) : xValue;
                    let tooltipText = `<span style="color:black;font-size:13px;"><span style="font-size:13.5px;">${xFormatted}</span><br/>`;

                    params.forEach((p, i) => {
                        const yFormatted = yFormatter ? yFormatter(p.data[1]) : p.data[1];
                        const marker = `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${_.get(
                            lineStyle,
                            ['color'],
                            p.color
                        )};"></span>`;
                        tooltipText += `${marker} ${p.seriesName} &nbsp; <strong>${yFormatted} ${_.get(yTooltipSuffix, [i], '')}</strong><br/>`;
                    });

                    tooltipText += '</span>';
                    return tooltipText;
                }
                return '';
            },
        },
        xAxis: {
            type: 'time',
            axisLabel: {
                formatter: xFormatter ? xFormatter : (val) => String(val),
                hideOverlap: true,
                fontSize: 13,
                color: 'black',
            },
            ...xAxisProps,
        },
        yAxis: {
            type: 'value',
            // ...(!_.get(yAxisProps, ['min']) && !_.get(yAxisProps, ['max']) ? getNiceYAxisBounds(series) : {}),
            // ...(dynamicYAxisBounds ? getNiceYAxisBounds(series) : {}),
            ...(_.some(series, (s) => !_.isEmpty(s.data)) ? { min: null, max: null } : { min: 0, max: 10 }),
            ...(addYAxisDynamicPadding ? getDynamicYAxisPadding(series) : {}),
            axisLabel: {
                formatter: yFormatter ? yFormatter : (val) => val,
                fontSize: 13,
                color: 'black',
            },
            ...yAxisProps,
        },
        dataZoom: [
            {
                type: 'inside',
                zoomOnMouseWheel,
                moveOnMouseMove: true,
                moveOnMouseWheel: true,
            },
        ],
        toolbox: {
            show: showToolBox,
            feature: {
                dataZoom: {
                    yAxisIndex: 'none',
                },
                saveAsImage: {
                    title: 'Download',
                    type: 'png',
                    name: title,
                    pixelRatio: 2,
                },
            },
        },
        series: series.map((s) => ({
            name: s.name,
            type: s.type || type,
            ...(step && { step: 'end' }),
            data: s.data,
            showSymbol: s.showSymbol || false,
            ...(lineType === 'area' ? { areaStyle: {} } : {}),
            ...(!_.isEmpty(lineStyle)
                ? {
                      lineStyle,
                      ...(_.get(lineStyle, ['color'])
                          ? {
                                emphasis: {
                                    itemStyle: {
                                        borderColor: _.get(lineStyle, ['color']),
                                    },
                                },
                            }
                          : {}),
                  }
                : {}),
            ...(annotations.length
                ? {
                      markLine: {
                          symbol: 'none',
                          label: {
                              show: true,
                              position: 'insideEnd',
                              padding: [3, 6],
                              borderRadius: 4,
                              fontWeight: 'bold',
                          },
                          data: annotations,
                      },
                  }
                : {}),
            ...(s.label || !_.isEmpty(label)
                ? {
                      label: {
                          ...(s.label || label),
                          formatter: (params) => {
                              const val = _.get(params, ['value', 1]);
                              return yFormatter ? yFormatter(val) : val;
                          },
                      },
                  }
                : {}),
        })),
    };

    return (
        <div className={classes.ChartContainer}>
            <div className={classes.Head}>
                <div>
                    <div className={classes.Title}>{title}</div>
                    {lastReported ? (
                        <div className={classes.LastReported}>
                            <Typography subtext content={'Last Reported: ' + (lastReported || ' - ')} />
                        </div>
                    ) : null}
                    {subtext ? (
                        <div className={classes.LastReported}>
                            <Typography subtext content={subtext} />
                        </div>
                    ) : null}
                </div>
                <div className={classes.ChartRight}>{rightSideElement && rightSideElement}</div>
            </div>
            <div className={classes.ChartParent}>
                <ReactEChartsCore className={classes.Chart} ref={chartRef} echarts={echarts} option={option} theme={'custom'} style={{ style }} />
            </div>
        </div>
    );
};

export default EChart;
